/* poo-health-checker.js — vanilla progressive enhancement for the Poo Health Checker page.
 * Replaces the framework runtime from the React prototype:
 *   1. the interactive poo checker (#sn-poo-health-checker [data-sn-widget="PooChecker"])
 *      — pet toggle, colour, consistency (1–7), symptoms → live plain-English verdict + Nova handoff
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * If the script never runs, the static checker shell + accordion questions are still visible.
 *
 * Both accordions on this page (the "colour by colour" deep-dives and the "Quick
 * answers" FAQ) are handled by the shared converted/_assets/sn-accordion.js. Their
 * answers are rendered into the static body.html (for SEO/crawlability), so this
 * file only owns the PooChecker widget.
 *
 * Logic ported verbatim from components/PooChecker.jsx + components/PooContent.jsx.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };

  // ---- Checker data (ported verbatim from PooChecker.jsx) -------------------
  var PC_COLOURS = [
    { id: 'brown',  label: T('Brown', 'Braun'),          sub: T('Healthy', 'Gesund'),        swatch: '#7A4F2A', base: 'fine' },
    { id: 'green',  label: T('Green', 'Grün'),          sub: T('Grass / transit', 'Gras / Passage'), swatch: '#5E7A36', base: 'watch' },
    { id: 'yellow', label: T('Yellow / orange', 'Gelb / orange'), sub: T('Diet / transit', 'Futter / Passage'),  swatch: '#D79A3A', base: 'watch' },
    { id: 'black',  label: T('Black / tarry', 'Schwarz / teerig'),  sub: T('Digested blood', 'Verdautes Blut'),  swatch: '#262220', base: 'now' },
    { id: 'red',    label: T('Red streaks', 'Rote Schlieren'),    sub: T('Fresh blood', 'Frisches Blut'),     swatch: '#9E3B2E', base: 'soon' },
    { id: 'white',  label: T('White / grey', 'Weiß / grau'),   sub: T('Pale / greasy', 'Blass / fettig'),   swatch: '#D9D2C4', base: 'soon' },
  ];

  var PC_CONSISTENCY = [
    { n: 1, label: T('Hard, dry pellets', 'Harte, trockene Kügelchen') },
    { n: 2, label: T('Firm, segmented - ideal', 'Fest, segmentiert - ideal') },
    { n: 3, label: T('Log-shaped, moist', 'Wurstförmig, feucht') },
    { n: 4, label: T('Very moist log', 'Sehr feucht, wurstförmig') },
    { n: 5, label: T('Soft blobs', 'Weiche Klumpen') },
    { n: 6, label: T('Mushy pile', 'Breiiger Haufen') },
    { n: 7, label: T('Liquid', 'Flüssig') },
  ];

  var PC_SYMPTOMS = [
    { id: 'blood',    label: T('Blood', 'Blut') },
    { id: 'mucus',    label: T('Mucus / jelly', 'Schleim / gelartig') },
    { id: 'worms',    label: T('Worms or rice-like specks', 'Würmer oder reisartige Körner') },
    { id: 'straining',label: T('Straining / no poo', 'Pressen / kein Kot') },
    { id: 'smell',    label: T('Strong change in smell', 'Starke Geruchsveränderung') },
    { id: 'none',     label: T('None of these', 'Keines davon') },
  ];

  var PC_RANK = { fine: 0, watch: 1, soon: 2, now: 3 };
  var PC_VERDICT = {
    fine:  { tag: T('Looks healthy', 'Sieht gesund aus'), headline: T('This looks like boring, healthy poo.', 'Das sieht nach langweiligem, gesundem Kot aus.'), tone: '#5B8C3E', bg: '#E6FFC6', fg: '#224031' },
    watch: { tag: T('Keep an eye on it', 'Im Auge behalten'), headline: T('Often not serious - but monitor closely and speak to your vet if anything changes.', 'Oft nicht ernst - beobachten Sie es aber genau und sprechen Sie mit Ihrer Praxis, wenn sich etwas ändert.'), tone: '#E8B23A', bg: '#FFF3D4', fg: '#5A4413' },
    soon:  { tag: T('Speak to your vet', 'Praxis kontaktieren'), headline: T('Worth contacting your vet for advice.', 'Es lohnt sich, Ihre Tierarztpraxis um Rat zu fragen.'), tone: '#E07A3E', bg: '#FFE3CE', fg: '#7A3B16' },
    now:   { tag: T('Call your vet now', 'Jetzt Tierarzt anrufen'), headline: T('Don’t wait this one out - call your vet today.', 'Warten Sie damit nicht - rufen Sie heute Ihre Praxis an.'), tone: '#C5462E', bg: '#FBD9D0', fg: '#7A241A' },
  };

  function pcEvaluate(opts) {
    var pet = opts.pet, colour = opts.colour, consistency = opts.consistency, symptoms = opts.symptoms;
    if (!colour) return null;
    var reasons = [];
    var level = 'fine';
    var bump = function (lv, why) {
      if (PC_RANK[lv] > PC_RANK[level]) level = lv;
      if (why) reasons.push(why);
    };

    var c = PC_COLOURS.filter(function (x) { return x.id === colour; })[0];
    // colour
    if (colour === 'brown') reasons.push(T('Chocolate brown is exactly what you want to see.', 'Schokoladenbraun ist genau das, was Sie sehen wollen.'));
    if (colour === 'green') bump('watch', T('Green is often caused by eating grass or by food moving through the intestine quickly.', 'Grün entsteht oft durch Grasfressen oder eine schnelle Darmpassage.'));
    if (colour === 'yellow') bump('watch', T('Yellow/orange often follows a diet change or faster transit; it may contain bile, and persistent changes should be checked.', 'Gelb/orange folgt oft einem Futterwechsel oder schnellerer Passage; es kann Gallenflüssigkeit enthalten, und anhaltende Veränderungen gehören abgeklärt.'));
    if (colour === 'white') bump('soon', T('Chalky white can follow a bone-heavy or calcium-rich diet; persistent very pale, grey or greasy stool warrants veterinary assessment.', 'Kalkiges Weiß kann auf knochen- oder kalziumreiche Fütterung folgen; anhaltend sehr blasser, grauer oder fettiger Kot gehört tierärztlich beurteilt.'));
    if (colour === 'red') bump('soon', T('A tiny amount of fresh blood can follow mild irritation or straining - more than a small amount, or repeated, needs a vet.', 'Eine winzige Menge frischen Blutes kann von leichter Reizung oder Pressen kommen - mehr als eine kleine Menge oder wiederholt gehört in die Praxis.'));
    if (colour === 'black') bump('now', T('Black, tarry stool ("melaena") can be digested blood from higher up the gut.', 'Schwarzer, teeriger Kot („Meläna“) kann verdautes Blut aus dem oberen Verdauungstrakt sein.'));

    // consistency
    if (consistency === 1) bump('watch', T('Hard pellets point to constipation or dehydration.', 'Harte Kügelchen deuten auf Verstopfung oder Austrocknung hin.'));
    if (consistency === 5 || consistency === 6) bump('watch', T('Softer than ideal - keep an eye on hydration and diet.', 'Weicher als ideal - achten Sie auf Flüssigkeit und Futter.'));
    if (consistency === 7) bump('soon', T('Liquid diarrhoea can dehydrate fast, especially in small or young pets.', 'Flüssiger Durchfall kann schnell zu Austrocknung führen, besonders bei kleinen oder jungen Tieren.'));

    // symptoms
    var has = function (id) { return symptoms.indexOf(id) !== -1; };
    if (has('blood')) bump('soon', T('Blood in the stool always warrants a closer look.', 'Blut im Kot verdient immer einen genaueren Blick.'));
    if (has('mucus')) bump('watch', T('A lot of jelly-like mucus, repeatedly, points to an irritated colon (colitis).', 'Viel gelartiger Schleim, wiederholt, deutet auf einen gereizten Dickdarm (Colitis) hin.'));
    if (has('worms')) bump('soon', T('Rice-like or spaghetti-like pieces mean a parasite conversation with your vet - different parasites need different treatments.', 'Reis- oder spaghettiartige Stücke bedeuten ein Gespräch über Parasiten mit Ihrer Praxis - verschiedene Parasiten brauchen verschiedene Mittel.'));
    if (has('smell')) bump('watch', T('A dramatic, sudden change in smell can accompany a digestive upset or parasite.', 'Eine drastische, plötzliche Geruchsveränderung kann eine Verdauungsstörung oder Parasiten begleiten.'));
    if (has('straining')) {
      if (pet === 'cat') bump('now', T('A cat straining repeatedly with nothing passing can be a urinary blockage - life-threatening within a short period of time, especially in males.', 'Eine Katze, die wiederholt presst, ohne dass etwas kommt, kann einen Harnröhrenverschluss haben - innerhalb kurzer Zeit lebensbedrohlich, besonders bei Katern.'));
      else bump('soon', T('Persistent or repeated straining, discomfort, vomiting, reduced appetite or lethargy needs a vet - and never give human laxatives or enemas.', 'Anhaltendes oder wiederholtes Pressen, Unwohlsein, Erbrechen, weniger Appetit oder Teilnahmslosigkeit gehören in die Praxis - und geben Sie niemals Abführmittel oder Klistiere für Menschen.'));
    }

    // escalating combinations
    if (consistency === 7 && (has('blood') || colour === 'red')) bump('now', T('Liquid stool with blood is a same-day vet call.', 'Flüssiger Kot mit Blut ist ein Anruf am selben Tag.'));
    if (consistency === 7 && has('smell')) bump('soon', T('Liquid plus a strong smell suggests a real digestive upset.', 'Flüssig plus starker Geruch deutet auf eine echte Verdauungsstörung hin.'));

    if (level === 'fine' && reasons.length === 0) reasons.push(T('Nothing here is raising a flag.', 'Hier fällt nichts auf.'));
    return { level: level, reasons: reasons, colour: c };
  }

  // ---- SVG fragments (ported from poo-illustrations.jsx + illustrations.jsx) -
  function stoolBlob(color, size) {
    var outline = 'rgba(28,25,23,0.22)';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 64 64" fill="none" aria-hidden="true">' +
      '<g>' +
      '<path d="M16 46c0-6 9-9 16-9s16 3 16 9c0 5-7 9-16 9s-16-4-16-9Z" fill="' + color + '" stroke="' + outline + '" stroke-width="1.5"></path>' +
      '<path d="M19 38c0-5 26-5 26 1" stroke="' + color + '" stroke-width="8" stroke-linecap="round" fill="none"></path>' +
      '<path d="M22 29c0-5 20-5 20 1" stroke="' + color + '" stroke-width="7" stroke-linecap="round" fill="none"></path>' +
      '<path d="M25 21c0-4 14-4 14 1" stroke="' + color + '" stroke-width="6" stroke-linecap="round" fill="none"></path>' +
      '<g stroke="' + outline + '" stroke-width="1.2" fill="none" opacity="0.5">' +
      '<path d="M19 38c0-5 26-5 26 1"></path><path d="M22 29c0-5 20-5 20 1"></path></g>' +
      '<circle cx="25" cy="47" r="2.4" fill="#fff" opacity="0.45"></circle>' +
      '</g></svg>';
  }

  function consistencyGlyph(level, size, color) {
    var body;
    if (level === 1) body = '<g fill="' + color + '"><circle cx="22" cy="34" r="7"></circle><circle cx="40" cy="30" r="7"></circle><circle cx="58" cy="35" r="7"></circle><circle cx="32" cy="46" r="6.5"></circle><circle cx="50" cy="46" r="6.5"></circle></g>';
    else if (level === 2) body = '<g fill="' + color + '"><path d="M14 32c0-8 52-8 52 0s-52 8-52 0Z"></path><g stroke="#fff" stroke-width="3"><path d="M30 25v14M44 25v14M56 26v12"></path></g></g>';
    else if (level === 3) body = '<g fill="' + color + '"><path d="M12 32c0-7 56-7 56 0s-56 7-56 0Z"></path><g stroke="#fff" stroke-width="2.2"><path d="M34 27v10M48 27v10"></path></g></g>';
    else if (level === 4) body = '<g fill="' + color + '"><path d="M10 32c0-7 60-7 60 0s-60 7-60 0Z"></path></g>';
    else if (level === 5) body = '<g fill="' + color + '"><ellipse cx="28" cy="34" rx="15" ry="11"></ellipse><ellipse cx="52" cy="33" rx="14" ry="11"></ellipse></g>';
    else if (level === 6) body = '<path d="M16 40c-2-10 8-16 16-13 4-7 16-6 19 0 9-1 14 6 11 12-2 5-9 6-16 6H26c-6 0-9-2-10-5Z" fill="' + color + '"></path>';
    else body = '<g fill="' + color + '"><path d="M14 42c2-7 10-9 14-7 2-5 10-6 13-1 7-2 12 2 12 6 4 0 7 2 6 5-1 2-5 3-11 3H22c-6 0-9-1-9-4 0-1 .5-2 1-2Z"></path><ellipse cx="60" cy="50" rx="5" ry="2.5" opacity="0.5"></ellipse></g>';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 80 64" fill="none" aria-hidden="true">' + body + '</svg>';
  }

  function novaOrb(size) {
    var face = '#224031', ring = '#CFFF90';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 120 120" fill="none" aria-hidden="true">' +
      '<circle cx="60" cy="60" r="56" fill="' + face + '"></circle>' +
      '<circle cx="60" cy="60" r="56" fill="none" stroke="' + ring + '" stroke-width="3" stroke-dasharray="6 9" opacity="0.7"></circle>' +
      '<circle cx="44" cy="54" r="7" fill="' + ring + '"></circle>' +
      '<circle cx="76" cy="54" r="7" fill="' + ring + '"></circle>' +
      '<circle cx="46" cy="56" r="2.6" fill="' + face + '"></circle>' +
      '<circle cx="78" cy="56" r="2.6" fill="' + face + '"></circle>' +
      '<path d="M44 74c5 6 27 6 32 0" stroke="' + ring + '" stroke-width="5" stroke-linecap="round" fill="none"></path>' +
      '<path d="M92 30l2.4 6.4L101 39l-6.6 2.4L92 48l-2.4-6.6L83 39l6.6-2.6Z" fill="' + ring + '"></path></svg>';
  }

  var ICON_PATHS = {
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"></path>',
    check: '<path d="M20 6 9 17l-5-5"></path>',
  };
  function icon(name, size, color) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + ICON_PATHS[name] + '</svg>';
  }

  function worryDot(level, size) {
    var map = { fine: '#5B8C3E', watch: '#E8B23A', soon: '#E07A3E', now: '#C5462E' };
    return '<span style="width:' + size + 'px;height:' + size + 'px;border-radius:999px;background:' + map[level] + ';display:inline-block;flex-shrink:0"></span>';
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // selection styling helper (mirrors `sel(active)` in PooChecker.jsx)
  function selCss(active) {
    return 'border:' + (active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)') + ';' +
      'background:' + (active ? 'var(--sn-lime-soft)' : '#fff') + ';' +
      'box-shadow:' + (active ? '0 0 0 4px rgba(207,255,144,0.4)' : 'none');
  }

  function stepHeader(n, title, hint) {
    return h('div', { css: 'display:flex;align-items:baseline;gap:12px;margin-bottom:16px' }, [
      h('span', { css: 'font-family:var(--sn-display);font-weight:700;font-size:13px;color:var(--sn-forest);background:var(--sn-lime-soft);border-radius:999px;width:26px;height:26px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0' }, [String(n)]),
      h('div', {}, [
        h('h3', { css: 'font-size:21px;line-height:1.1' }, [title]),
        hint ? h('span', { css: 'font-size:13.5px;color:var(--sn-ink-60)' }, [hint]) : null,
      ]),
    ]);
  }

  // ---- Checker widget -------------------------------------------------------
  function initChecker(root) {
    var state = { pet: 'dog', colour: null, consistency: null, symptoms: [] };

    function toggleSymptom(id) {
      var prev = state.symptoms;
      if (id === 'none') {
        state.symptoms = prev.indexOf('none') !== -1 ? [] : ['none'];
        return;
      }
      var next = prev.filter(function (s) { return s !== 'none'; });
      state.symptoms = next.indexOf(id) !== -1
        ? next.filter(function (s) { return s !== id; })
        : next.concat([id]);
    }

    // ---- Controls column ----
    var petToggle = h('div', { css: 'display:flex;gap:8px;background:var(--sn-cream-2);border-radius:999px;padding:5px;width:fit-content;margin-bottom:30px' },
      ['dog', 'cat'].map(function (p) {
        return h('button', {
          attrs: { 'data-pc-pet': p },
          css: 'border:none;cursor:pointer;border-radius:999px;padding:9px 26px;font-family:var(--sn-display);font-weight:600;font-size:15px;text-transform:capitalize;' +
            'background:' + (state.pet === p ? 'var(--sn-forest)' : 'transparent') + ';' +
            'color:' + (state.pet === p ? 'var(--sn-cream)' : 'var(--sn-ink-60)'),
          on: { click: function () { state.pet = p; syncAll(); } },
        }, [p]);
      }));

    var colourGrid = h('div', { css: 'display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:32px' },
      PC_COLOURS.map(function (c) {
        return h('button', {
          attrs: { 'data-pc-colour': c.id },
          css: 'cursor:pointer;border-radius:16px;padding:14px 12px;text-align:center;display:flex;flex-direction:column;align-items:center;gap:6px;' + selCss(false),
          on: { click: function () { state.colour = c.id; syncAll(); } },
        }, [
          h('span', { css: 'display:inline-flex', html: stoolBlob(c.swatch, 46) }),
          h('span', { css: 'font-family:var(--sn-display);font-weight:600;font-size:14px' }, [c.label]),
          h('span', { css: 'font-size:11.5px;color:var(--sn-ink-60)' }, [c.sub]),
        ]);
      }));

    var consistencyGrid = h('div', { css: 'display:grid;grid-template-columns:repeat(7,1fr);gap:7px;margin-bottom:32px' },
      PC_CONSISTENCY.map(function (c) {
        return h('button', {
          attrs: { 'data-pc-consistency': String(c.n), title: c.label },
          css: 'cursor:pointer;border-radius:13px;padding:10px 4px 8px;display:flex;flex-direction:column;align-items:center;gap:4px;' + selCss(false),
          on: { click: function () { state.consistency = c.n; syncAll(); } },
        }, [
          h('span', { attrs: { 'data-pc-glyph': '' }, css: 'display:inline-flex', html: consistencyGlyph(c.n, 42, '#9a958f') }),
          h('span', { css: 'font-family:var(--sn-display);font-weight:700;font-size:13px' }, [String(c.n)]),
        ]);
      }));

    var consistencyNote = h('div', { attrs: { 'data-pc-cnote': '' }, css: 'margin-top:-22px;margin-bottom:30px;font-size:13.5px;color:var(--sn-ink-60);display:none' });

    var symptomRow = h('div', { css: 'display:flex;flex-wrap:wrap;gap:9px' },
      PC_SYMPTOMS.map(function (s) {
        return h('button', {
          attrs: { 'data-pc-symptom': s.id },
          css: 'cursor:pointer;border-radius:999px;padding:11px 18px;font-size:14.5px;font-weight:500;font-family:var(--sn-body);display:inline-flex;align-items:center;gap:8px;' +
            'border:2px solid var(--sn-line);background:#fff;color:var(--sn-ink)',
          on: { click: function () { toggleSymptom(s.id); syncAll(); } },
        }, [h('span', { attrs: { 'data-pc-slabel': '' } }, [s.label])]);
      }));

    var controls = h('div', { css: 'padding:34px 38px' }, [
      petToggle,
      stepHeader(1, 'What colour is it?'),
      colourGrid,
      stepHeader(2, 'How firm is it?', 'Purina 7-point chart · 1 = hard pellets · 2 = ideal · 7 = liquid'),
      consistencyGrid,
      consistencyNote,
      stepHeader(3, 'Anything else?', 'Select all that apply'),
      symptomRow,
    ]);

    // ---- Verdict column ----
    var verdict = h('div', { attrs: { 'data-pc-verdict': '' }, css: 'padding:34px 32px;display:flex;flex-direction:column' });

    var grid = h('div', {
      css: 'display:grid;grid-template-columns:1.35fr 1fr;gap:0;border-radius:28px;overflow:hidden;border:1px solid var(--sn-line);box-shadow:0 30px 70px -45px rgba(28,25,23,0.5);background:#fff',
    }, [controls, verdict]);

    function renderVerdict() {
      var result = pcEvaluate(state);
      var v = result ? PC_VERDICT[result.level] : null;
      verdict.innerHTML = '';
      verdict.style.cssText = 'background-color:' + (v ? v.bg : '#224031') + ';color:' + (v ? v.fg : 'var(--sn-cream)') + ';padding:34px 32px;display:flex;flex-direction:column';

      if (!result) {
        verdict.appendChild(h('div', { css: 'margin:auto;text-align:center;max-width:260px' }, [
          h('span', { css: 'display:inline-flex', html: novaOrb(92) }),
          h('h3', { css: 'font-size:22px;margin:18px 0 8px;color:var(--sn-cream)' }, ['Tell me what you’re looking at']),
          h('p', { css: 'font-size:14.5px;color:rgba(255,248,240,0.72);line-height:1.55' }, ['Pick a colour to get a plain-English read on whether it’s normal - and what to do if it isn’t.']),
        ]));
        return;
      }

      var novaQ = 'My ' + state.pet + ' just did ' + PC_COLOURS.filter(function (c) { return c.id === state.colour; })[0].label.toLowerCase() + ' poo' +
        (state.consistency ? ', ' + PC_CONSISTENCY[state.consistency - 1].label.toLowerCase() : '') +
        (state.symptoms.length && state.symptoms.indexOf('none') === -1
          ? ', with ' + state.symptoms.map(function (s) {
              return PC_SYMPTOMS.filter(function (x) { return x.id === s; })[0].label.toLowerCase();
            }).join(' and ')
          : '') +
        '. Should I worry?';

      var reasonList = h('ul', { css: 'list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:12px' },
        result.reasons.map(function (r) {
          return h('li', { css: 'display:flex;gap:10px;font-size:14.5px;line-height:1.5' }, [
            h('span', { css: 'margin-top:6px;flex-shrink:0', html: worryDot(result.level, 7) }),
            h('span', {}, [r]),
          ]);
        }));

      verdict.appendChild(h('div', { css: 'display:flex;flex-direction:column;height:100%' }, [
        h('span', { cls: 'sn-eyebrow', css: 'color:' + v.tone + ';margin-bottom:12px', html: worryDot(result.level, 12) + ' ' + v.tag }),
        h('h3', { css: 'font-size:27px;line-height:1.12;margin-bottom:16px' }, [v.headline]),
        reasonList,
        h('div', { css: 'margin-top:auto;padding-top:24px' }, [
          h('div', {
            css: 'background:rgba(255,255,255,0.55);border-radius:16px;padding:16px 18px;margin-bottom:14px;border:1px solid ' + v.tone + '33',
          }, [
            h('div', { css: 'display:flex;align-items:center;gap:8px;margin-bottom:8px' }, [
              h('span', { css: 'display:inline-flex', html: novaOrb(30) }),
              h('strong', { css: 'font-family:var(--sn-display);font-size:14.5px' }, ['Ask Nova, with your pet’s profile']),
            ]),
            h('p', { attrs: { 'data-pc-novaq': '' }, css: 'font-size:13px;font-style:italic;line-height:1.45;opacity:0.85' }, ['"' + novaQ + '"']),
          ]),
          h('a', { cls: 'sn-btn sn-btn--primary', css: 'width:100%;justify-content:center', html: 'Ask Nova about this ' + icon('arrow', 18, 'currentColor') }),
          h('p', { css: 'font-size:11.5px;text-align:center;margin-top:12px;opacity:0.62' }, ['Not a diagnosis. If your pet seems unwell in any way - or you are unsure - contact your vet.']),
        ]),
      ]));
    }

    function syncAll() {
      // pet toggle
      root.querySelectorAll('[data-pc-pet]').forEach(function (btn) {
        var active = btn.getAttribute('data-pc-pet') === state.pet;
        btn.style.background = active ? 'var(--sn-forest)' : 'transparent';
        btn.style.color = active ? 'var(--sn-cream)' : 'var(--sn-ink-60)';
      });
      // colour
      root.querySelectorAll('[data-pc-colour]').forEach(function (btn) {
        applySel(btn, btn.getAttribute('data-pc-colour') === state.colour);
      });
      // consistency
      root.querySelectorAll('[data-pc-consistency]').forEach(function (btn) {
        var n = parseInt(btn.getAttribute('data-pc-consistency'), 10);
        var active = state.consistency === n;
        applySel(btn, active);
        btn.querySelector('[data-pc-glyph]').innerHTML = consistencyGlyph(n, 42, active ? 'var(--sn-forest)' : '#9a958f');
      });
      // consistency note
      if (state.consistency) {
        consistencyNote.style.display = '';
        consistencyNote.innerHTML = 'Selected: <strong style="color:var(--sn-ink)">' + state.consistency + ' · ' + PC_CONSISTENCY[state.consistency - 1].label + '</strong>';
      } else {
        consistencyNote.style.display = 'none';
        consistencyNote.innerHTML = '';
      }
      // symptoms
      root.querySelectorAll('[data-pc-symptom]').forEach(function (btn) {
        var id = btn.getAttribute('data-pc-symptom');
        var active = state.symptoms.indexOf(id) !== -1;
        btn.style.border = active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)';
        btn.style.background = active ? 'var(--sn-forest)' : '#fff';
        btn.style.color = active ? 'var(--sn-cream)' : 'var(--sn-ink)';
        var labelText = PC_SYMPTOMS.filter(function (s) { return s.id === id; })[0].label;
        btn.innerHTML = (active ? icon('check', 15, 'var(--sn-lime)') : '') +
          '<span data-pc-slabel>' + labelText + '</span>';
      });
      renderVerdict();
    }

    function applySel(btn, active) {
      btn.style.border = active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)';
      btn.style.background = active ? 'var(--sn-lime-soft)' : '#fff';
      btn.style.boxShadow = active ? '0 0 0 4px rgba(207,255,144,0.4)' : 'none';
    }

    root.innerHTML = '';
    root.appendChild(grid);
    syncAll();
  }

  // ---- Boot -----------------------------------------------------------------
  // Both accordions (the "colour by colour" deep-dives and the "Quick answers"
  // FAQ) are owned by the shared converted/_assets/sn-accordion.js — this file
  // only initialises the PooChecker widget.
  function boot() {
    var page = document.getElementById('sn-poo-health-checker');
    if (!page) return;
    var widget = page.querySelector('[data-sn-widget="PooChecker"]');
    if (widget) initChecker(widget);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
